import { useMemo, useState, useEffect } from "react";
import { observer } from "mobx-react-lite";
import { FormField } from "@/components/common";
import AppDialog from "@/components/common/AppDialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { OrgTreeSelect } from "@/components/common/OrgTreeSelect";
import { SelectField } from "@/components/common/SelectField";
import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Info,
  Loader2,
  RefreshCw,
  Search,
  Zap,
} from "lucide-react";
import { useTran } from "@/hooks";
import OrgStatusTag from "@/features/organization/components/OrgStatusTag";
import {
  useDisclosureStore,
  useEntryDataStore,
  useEntryUIStore,
} from "../../hooks/useDataEntryStore";
import {
  fetchBulkSyncPreview,
  confirmBulkSync,
} from "../../operations/nxmap-bulk-sync.ops";
import { formatMetricValue } from "../../domains/dataEntry";
import { SearchableSelect } from "@/components/common";
import { loadReportingYears } from "@/features/reporting-boundary/operations/reporting-boundary.ops";
import { formatReportingYearPeriod } from "@/lib/utils/period-constants";
import { showToast } from "@/lib/utils/toast";
import { findOrgPathWithStatus } from "@/components/common/organization.utils";

const findOrgPath = findOrgPathWithStatus;

const OrgBreadcrumb = ({ path = [], leafClassName = "font-semibold" }) => {
  if (!path.length) return null;
  return (
    <span className="inline-flex flex-wrap items-center gap-x-1 text-xs">
      {path.map((node, i) => {
        const isLast = i === path.length - 1;
        return (
          <span
            key={`${node.id}-${i}`}
            className="inline-flex items-center gap-x-1"
          >
            {i > 0 && <span className="text-muted-foreground/50">›</span>}
            <div className="inline-flex items-center gap-0.5">
              <span
                className={isLast ? leafClassName : "text-muted-foreground"}
              >
                {node.name}
              </span>
              <OrgStatusTag
                status={node.status}
                inactiveDate={node.inactiveDate}
                className="text-[10px]"
              />
            </div>
          </span>
        );
      })}
    </span>
  );
};

const SyncStatusBadge = ({ status, count, compact = false }) => {
  if (!count) return null;
  const styles = {
    new: "bg-emerald-100 text-emerald-700",
    updated: "bg-amber-100 text-amber-700",
    unchanged: "bg-muted text-muted-foreground",
    deleted: "bg-pink-100 text-pink-700 line-through",
  };
  return (
    <span
      className={`text-[11px] px-1.5 py-0.5 rounded tabular-nums ${styles[status]}`}
      title={status}
    >
      {compact ? count : `${count} ${status}`}
    </span>
  );
};

const OrgYearGroup = ({ group, orgPath }) => {
  const [expanded, setExpanded] = useState(false);
  const Chevron = expanded ? ChevronDown : ChevronRight;
  const hasPath = orgPath && orgPath.length > 0;

  const groupSummary = group.disclosures.reduce(
    (acc, d) => ({
      new: acc.new + (d.summary?.new || 0),
      updated: acc.updated + (d.summary?.updated || 0),
      unchanged: acc.unchanged + (d.summary?.unchanged || 0),
      deleted: acc.deleted + (d.summary?.deleted || 0),
    }),
    { new: 0, updated: 0, unchanged: 0, deleted: 0 },
  );

  return (
    <div className="border border-border rounded-md">
      <button
        type="button"
        className="flex w-full items-start gap-2 px-3 py-2 text-left hover:bg-muted/40 text-sm"
        onClick={() => setExpanded((v) => !v)}
      >
        <Chevron size={14} className="mt-0.5 text-muted-foreground shrink-0" />
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <div className="flex items-center gap-2">
            <span className="font-medium truncate">{group.orgName}</span>
            <Badge variant="outline" className="text-[10px] shrink-0">
              {group.year}
            </Badge>
          </div>
          {hasPath && orgPath.length > 1 && (
            <OrgBreadcrumb path={orgPath} leafClassName="text-foreground" />
          )}
        </div>
        <div className="ml-2 flex shrink-0 items-center gap-1">
          <SyncStatusBadge status="new" count={groupSummary.new} compact />
          <SyncStatusBadge
            status="updated"
            count={groupSummary.updated}
            compact
          />
          <SyncStatusBadge
            status="unchanged"
            count={groupSummary.unchanged}
            compact
          />
          <SyncStatusBadge
            status="deleted"
            count={groupSummary.deleted}
            compact
          />
          <span className="ml-1 text-xs text-muted-foreground">
            {group.disclosures.length} disclosure(s)
          </span>
        </div>
      </button>
      {expanded && (
        <div className="border-t divide-y text-xs">
          {group.disclosures.map((d) => (
            <div
              key={d.disclosureCode}
              className="flex items-center gap-2 px-4 py-2"
            >
              <Badge variant="outline" className="text-[10px] font-mono">
                {d.disclosureCode}
              </Badge>
              <span className="flex-1 truncate text-muted-foreground">
                {d.disclosureTitle}
              </span>
              {d.matches?.[0] && (
                <span className="text-xs font-semibold tabular-nums">
                  {formatMetricValue(d.matches[0].value)}{" "}
                  {d.matches[0].valueUnit}
                </span>
              )}
              <div className="flex items-center gap-1.5">
                <SyncStatusBadge status="new" count={d.summary.new} />
                <SyncStatusBadge status="updated" count={d.summary.updated} />
                <SyncStatusBadge
                  status="unchanged"
                  count={d.summary.unchanged}
                />
                <SyncStatusBadge status="deleted" count={d.summary.deleted} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const NxmapBulkSyncDialog = observer(() => {
  const { t } = useTran();
  const { orgTree } = useEntryDataStore();
  const { showNxmapBulkSyncDialog, setShowNxmapBulkSyncDialog } =
    useEntryUIStore();

  const [state, setState] = useState("idle");
  const [preview, setPreview] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [selectedOrgId, setSelectedOrgId] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const [groupSearch, setGroupSearch] = useState("");

  const [reportingYears, setReportingYears] = useState([]);
  const [selectedReportingYear, setSelectedReportingYear] = useState(null);
  const [reportingYearsLoading, setReportingYearsLoading] = useState(false);

  const reportingYearOptions = useMemo(
    () =>
      reportingYears
        .slice()
        .sort((a, b) => Number(b.startYear) - Number(a.startYear))
        .map((item) => ({
          value: item._id,
          label: formatReportingYearPeriod(item),
        })),
    [reportingYears],
  );

  const fetchReportingYears = async () => {
    try {
      const data = await loadReportingYears();
      setReportingYears(data);
    } catch (error) {
      console.error("Failed to fetch reporting years:", error);

      showToast.error("Failed to load reporting years. Please try again.");

      setReportingYears([]); // safe fallback
    }
  };

  useEffect(() => {
    if (!showNxmapBulkSyncDialog) return;
    if (reportingYears.length > 0) return;

    fetchReportingYears();
  }, [showNxmapBulkSyncDialog, reportingYears.length]);

  // JJSEA is the only NxMap-mapped framework wired up so far; pin it here
  // until we expose a framework picker for the rest.
  const framework = "JJ-Group";

  // Show Level 1 orgs with their Level 2 children — strip deeper levels
  // Filter organization tree based on selected reporting year
  const filterTree = (nodes, allowedIds) => {
    return (nodes || []).reduce((result, node) => {
      const children = filterTree(node.children || [], allowedIds);

      if (allowedIds.has(String(node.id)) || children.length > 0) {
        result.push({
          ...node,
          children,
        });
      }

      return result;
    }, []);
  };

  const level1OrgTree = useMemo(() => {
    let tree = orgTree;

    // Filter only when reporting year is selected
    if (selectedReportingYear?.organizations?.length) {
      const allowedIds = new Set(
        selectedReportingYear.organizations.map((org) => String(org._id)),
      );

      tree = filterTree(orgTree, allowedIds);
    }

    // Always show only Level 1 -> Level 2
    return tree.map((node) => ({
      ...node,
      children: (node.children || []).map((child) => ({
        ...child,
        children: [],
      })),
    }));
  }, [orgTree, selectedReportingYear]);

  const selectedOrgPath = useMemo(
    () => findOrgPath(orgTree, selectedOrgId),
    [orgTree, selectedOrgId],
  );

  // Whether the picked node has children visible in the picker (Level 1 with
  // Level 2 children). Picker leaves (Level 2 here) expand to their real
  // subtree on the server; picker parents fetch only themselves.
  const pickedHasChildrenInPicker = useMemo(() => {
    if (!selectedOrgId) return false;
    const findNode = (nodes) => {
      for (const node of nodes || []) {
        if (String(node?.id) === String(selectedOrgId)) return node;
        const found = findNode(node?.children);
        if (found) return found;
      }
      return null;
    };
    const node = findNode(level1OrgTree);
    return Boolean(node?.children?.length);
  }, [level1OrgTree, selectedOrgId]);

  const includeSubOrgs = selectedOrgId ? !pickedHasChildrenInPicker : false;

  const summary = preview?.summary;
  const previewGroups = useMemo(() => preview?.groups || [], [preview]);
  const filteredGroups = useMemo(() => {
    const query = groupSearch.trim().toLowerCase();
    if (!query) return previewGroups;
    return previewGroups.filter((group) =>
      group.orgName?.toLowerCase().includes(query),
    );
  }, [groupSearch, previewGroups]);
  const hasChanges =
    summary && (summary.new > 0 || summary.updated > 0 || summary.deleted > 0);
  const busy = state === "loading" || state === "syncing";

  const handlePreview = async () => {
    setState("loading");
    setErrorMsg("");
    setGroupSearch("");
    try {
      const result = await fetchBulkSyncPreview(
        selectedOrgId,
        selectedYear,
        includeSubOrgs,
      );
      setPreview(result);
      setState("preview");
    } catch (err) {
      setErrorMsg(err?.message || "Failed to fetch");
      setState("error");
    }
  };

  const handleConfirm = async () => {
    setState("syncing");
    try {
      await confirmBulkSync(preview);
      handleClose();
    } catch (err) {
      setErrorMsg(err?.message || "Failed to sync");
      setState("preview");
    }
  };

  const handleClose = () => {
    setState("idle");
    setPreview(null);
    setErrorMsg("");
    setSelectedOrgId("");
    setSelectedYear("");
    setGroupSearch("");
    setShowNxmapBulkSyncDialog(false);
  };

  const handleBack = () => {
    setState("idle");
    setPreview(null);
    setErrorMsg("");
    setGroupSearch("");
  };

  const handleReportingYearChange = (reportingYearId) => {
    const reportingYear = reportingYears.find(
      (item) => String(item._id) === String(reportingYearId),
    );
    setSelectedReportingYear(reportingYear || null);
    setSelectedYear(reportingYear?.startYear || undefined);
    setReportingYearsLoading(false);
  };

  const canFetch = Boolean(framework && selectedOrgId && selectedYear);
  //const canFetch = Boolean(selectedOrgId && selectedYear);

  return (
    <AppDialog
      open={!!showNxmapBulkSyncDialog}
      onOpenChange={(open) => !open && !busy && handleClose()}
      size="md"
      lockClose={busy}
      header={
        <div className="flex items-center gap-2">
          <Zap size={16} className="text-primary" />
          <span className="text-lg font-semibold">{t("NXMap Bulk Sync")}</span>
        </div>
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={handleClose} disabled={busy}>
            {t("Cancel")}
          </Button>

          {state === "preview" && (
            <Button variant="outline" onClick={handleBack} disabled={busy}>
              <ChevronLeft size={14} />
              {t("Back")}
            </Button>
          )}

          {(state === "idle" || state === "error") && (
            <Button onClick={handlePreview} disabled={!canFetch}>
              <Zap size={14} />
              {t("Fetch Preview")}
            </Button>
          )}

          {(state === "preview" || state === "syncing") && hasChanges && (
            <Button onClick={handleConfirm} disabled={busy}>
              {state === "syncing" ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <RefreshCw size={14} />
              )}
              {summary?.updated > 0 || summary?.deleted > 0
                ? t("Sync All")
                : t("Import All")}
            </Button>
          )}
        </div>
      }
    >
      {state === "idle" && (
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>
              {t("Reporting Year")}
              <span className="ml-1 text-destructive">*</span>
            </Label>
            {reportingYearsLoading ? (
              <Skeleton className="h-9 w-full rounded-md" />
            ) : (
              <SearchableSelect
                value={selectedReportingYear?._id || ""}
                onChange={handleReportingYearChange}
                options={reportingYearOptions}
                placeholder={t("Select Reporting Year")}
                searchPlaceholder={t("Search Reporting Year")}
                maxLabelLength={30}
              />
            )}
          </div>
          <div className="space-y-2">
            <Label>
              {t("Organization")}
              <span className="ml-1 text-destructive">*</span>
            </Label>
            <OrgTreeSelect
              tree={level1OrgTree}
              value={selectedOrgId}
              onChange={setSelectedOrgId}
              placeholder={t("Select organization")}
              showAllOption={false}
              className="w-full"
            />
            {selectedOrgId && (
              <div className="flex items-start gap-1.5 text-xs text-muted-foreground">
                <Info size={12} className="mt-0.5 shrink-0" />
                <span>
                  {includeSubOrgs
                    ? t("Fetching this organization and its sub-organizations.")
                    : t(
                        "Fetching only this organization. Sub-organizations are excluded.",
                      )}
                </span>
              </div>
            )}
          </div>
          <p className="text-sm text-muted-foreground">
            {t(
              "Sync NXMap-mappable metrics for the selected scope and reporting year.",
            )}
          </p>
        </div>
      )}

      {state === "loading" && (
        <div className="flex flex-col items-center gap-3 py-8 text-muted-foreground">
          <Loader2 size={28} className="animate-spin text-primary" />
          <p className="text-sm font-medium">
            {t("Fetching emissions from NXMap... This may take a moment.")}
          </p>
        </div>
      )}

      {state === "error" && (
        <div className="flex flex-col gap-4 py-2">
          <div className="space-y-2">
            <Label>
              {t("Reporting Year")}
              <span className="ml-1 text-destructive">*</span>
            </Label>
            {reportingYearsLoading ? (
              <Skeleton className="h-9 w-full rounded-md" />
            ) : (
              <SearchableSelect
                value={selectedReportingYear?._id || ""}
                onChange={handleReportingYearChange}
                options={reportingYearOptions}
                placeholder={t("Select Reporting Year")}
                searchPlaceholder={t("Search Reporting Year")}
                maxLabelLength={30}
              />
            )}
          </div>
          <div className="space-y-2">
            <Label>
              {t("Organization")}
              <span className="ml-1 text-destructive">*</span>
            </Label>
            <OrgTreeSelect
              tree={level1OrgTree}
              value={selectedOrgId}
              onChange={setSelectedOrgId}
              placeholder={t("Select organization")}
              showAllOption={false}
              className="w-full"
            />
            {selectedOrgId && (
              <div className="flex items-start gap-1.5 text-xs text-muted-foreground">
                <Info size={12} className="mt-0.5 shrink-0" />
                <span>
                  {includeSubOrgs
                    ? t("Fetching this organization and its sub-organizations.")
                    : t(
                        "Fetching only this organization. Sub-organizations are excluded.",
                      )}
                </span>
              </div>
            )}
          </div>
          <div className="flex flex-col items-center gap-3 py-2 text-destructive">
            <AlertCircle size={28} />
            <p className="text-sm text-center">{errorMsg}</p>
          </div>
        </div>
      )}

      {(state === "preview" || state === "syncing") && preview && (
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-border/60 bg-muted/30 px-3 py-2 text-xs">
            <span className="inline-flex items-center gap-1.5">
              <span className="text-muted-foreground">{t("Year")}:</span>
              <Badge variant="outline" className="text-[11px]">
                {selectedYear || "—"}
              </Badge>
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="text-muted-foreground">
                {t("Organization")}:
              </span>
              {selectedOrgPath.length ? (
                <OrgBreadcrumb path={selectedOrgPath} />
              ) : (
                <span className="font-semibold">{selectedOrgId || "—"}</span>
              )}
            </span>
          </div>
          <div className="flex items-center gap-3 rounded-lg bg-muted/50 px-4 py-3">
            <CheckCircle2 size={18} className="text-primary" />
            <div className="flex items-center gap-2 text-sm">
              <SyncStatusBadge status="new" count={summary.new} />
              <SyncStatusBadge status="updated" count={summary.updated} />
              <SyncStatusBadge status="unchanged" count={summary.unchanged} />
              <SyncStatusBadge status="deleted" count={summary.deleted} />
            </div>
            <span className="text-xs text-muted-foreground ml-auto">
              {previewGroups.length} org-year group(s)
            </span>
          </div>

          {previewGroups.length > 0 && (
            <FormField
              value={groupSearch}
              onChange={(e) => setGroupSearch(e.target.value)}
              placeholder={t("Search organizations...")}
              leftIcon={<Search size={16} />}
            />
          )}

          <div className="flex flex-col gap-2">
            {filteredGroups.map((group) => (
              <OrgYearGroup
                key={`${group.orgId}-${group.year}`}
                group={group}
                orgPath={findOrgPath(orgTree, group.orgId)}
              />
            ))}
            {!previewGroups.length && (
              <p className="py-6 text-center text-sm text-muted-foreground">
                {t("No NXMap data found for assigned disclosures.")}
              </p>
            )}
            {!!previewGroups.length && !filteredGroups.length && (
              <p className="py-6 text-center text-sm text-muted-foreground">
                {t("No organizations match your search.")}
              </p>
            )}
          </div>
        </div>
      )}
    </AppDialog>
  );
});

export default NxmapBulkSyncDialog;
